// ---------------------------------------------------------------
// Fleet Overview — the screen a dispatcher leaves open all day.
//
// Samsara's overview is a LIST: 331 assets, scroll to find yours. It answers
// "what is everything doing?" Ours answers a different question first: "what
// needs me right now?" — then lets you drill into everything else.
//
// LAYOUT REASONING
//  1. ATTENTION first, at the top, always. If it's empty that's the headline,
//     and an empty attention queue should feel like good news rather than a
//     broken page.
//  2. Numbers that change decisions, not vanity metrics. "58 at terminal" tells
//     a dispatcher there's capacity; "1,184,063 miles driven" does not.
//  3. HOURS next, because a driver running out of hours is a load that stops
//     moving, and it's the most time-sensitive thing on the screen.
//  4. Everything else below, dense but scannable.
//
// Density is deliberate — the request was "as much useful information as
// possible" — but every element earns its place by answering a question a
// dispatcher actually asks.
// ---------------------------------------------------------------
import React, { useState, useEffect, useCallback } from 'react';
import { loadLiveFleet, loadHos, loadAttention } from '../fleetStore';

const PANEL = '#0c1220';
const TXT = '#e8ebf5';
const MUTED = '#9aa0b4';
const HAIR = '1px solid rgba(255,255,255,0.09)';
const RED = '#ef4444';
const AMBER = '#f59e0b';
const GREEN = '#22c55e';
const BLUE = '#60a5fa';

const card = { background: PANEL, border: HAIR, borderRadius: '14px', padding: '16px' };
const h = { fontSize: '13px', fontWeight: 800, letterSpacing: '.02em', marginBottom: '10px' };
const lbl = {
  fontSize: '10px', color: MUTED, fontWeight: 700,
  textTransform: 'uppercase', letterSpacing: '.05em',
};

const hhmm = (min) => {
  if (min == null) return '—';
  if (min <= 0) return '0:00';
  return `${Math.floor(min / 60)}:${String(min % 60).padStart(2, '0')}`;
};

// ---------- a number that means something ----------
function Metric({ label, value, tone, sub, wide }) {
  return (
    <div style={{ ...card, padding: '13px 15px', minWidth: wide ? '160px' : '108px', flex: wide ? 1.4 : 1 }}>
      <div style={lbl}>{label}</div>
      <div style={{ fontSize: '25px', fontWeight: 800, color: tone || TXT, lineHeight: 1.15, marginTop: '3px' }}>
        {value}
      </div>
      {sub ? <div style={{ fontSize: '11px', color: MUTED, marginTop: '2px' }}>{sub}</div> : null}
    </div>
  );
}

// ---------- the attention queue ----------
function Attention({ data, err }) {
  const findings = (data && data.findings) || [];
  const sum = (data && data.summary) || {};

  if (err) {
    return (
      <div style={{ ...card, borderColor: 'rgba(239,68,68,0.3)' }}>
        <div style={{ ...h, color: RED }}>Review unavailable</div>
        <div style={{ fontSize: '13px', color: MUTED }}>{err}</div>
      </div>
    );
  }

  if (!findings.length) {
    // An empty queue is the goal, so it should read as reassurance rather than
    // an empty state that looks broken.
    return (
      <div style={{ ...card, borderColor: 'rgba(34,197,94,0.28)', background: 'rgba(34,197,94,0.06)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={{ fontSize: '20px' }}>✓</span>
          <div>
            <div style={{ fontWeight: 800, fontSize: '15px', color: GREEN }}>Nothing needs you right now</div>
            <div style={{ fontSize: '12px', color: MUTED }}>
              {sum.loadsReviewed != null ? `${sum.loadsReviewed} active load(s) checked — ` : ''}
              temperature, appointments, paperwork and hours all clear.
            </div>
          </div>
        </div>
      </div>
    );
  }

  const toneOf = (sev) => (sev === 3 ? RED : sev === 2 ? AMBER : BLUE);
  return (
    <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '14px 16px', borderBottom: HAIR }}>
        <div style={{ ...h, marginBottom: 0, flex: 1 }}>Needs attention</div>
        {sum.critical > 0 && (
          <span style={{ background: 'rgba(239,68,68,0.16)', color: RED, borderRadius: '999px', padding: '3px 10px', fontSize: '11px', fontWeight: 800 }}>
            {sum.critical} critical
          </span>
        )}
        {sum.warning > 0 && (
          <span style={{ background: 'rgba(245,158,11,0.16)', color: AMBER, borderRadius: '999px', padding: '3px 10px', fontSize: '11px', fontWeight: 800 }}>
            {sum.warning} warning
          </span>
        )}
      </div>
      <div style={{ maxHeight: '340px', overflowY: 'auto' }}>
        {findings.map((f, i) => (
          <div key={i} style={{ padding: '12px 16px', borderTop: i ? HAIR : 'none', display: 'flex', gap: '11px' }}>
            <div style={{ width: '4px', borderRadius: '2px', background: toneOf(f.severity), flexShrink: 0 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 700, fontSize: '13px', color: TXT }}>{f.title}</div>
              <div style={{ fontSize: '12px', color: MUTED, marginTop: '2px', lineHeight: 1.45 }}>{f.detail}</div>
              {f.draft ? (
                <div style={{ marginTop: '7px', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: '10px', color: MUTED, fontWeight: 700, textTransform: 'uppercase' }}>
                    Draft ready → {f.draft.to}
                  </span>
                  <button style={{
                    background: 'rgba(255,255,255,0.08)', color: TXT, border: HAIR,
                    borderRadius: '7px', padding: '4px 10px', fontSize: '11px', fontWeight: 700, cursor: 'pointer',
                  }}>Review &amp; send</button>
                </div>
              ) : null}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------- hours of service ----------
// Ranked by who stops driving soonest. That's the dispatch question; the
// compliance report can live elsewhere.
function Hours({ hos, err }) {
  if (err) return <div style={card}><div style={{ ...h, color: MUTED }}>Hours</div><div style={{ fontSize: '12px', color: MUTED }}>{err}</div></div>;
  if (!hos) return <div style={card}><div style={h}>Hours</div><div style={{ fontSize: '12px', color: MUTED }}>Loading…</div></div>;

  const c = hos.counts || {};
  const soon = (hos.rows || []).filter((r) => r.driving && r.worstRemainingMin != null).slice(0, 8);
  const toneFor = (m) => (m <= 0 ? RED : m <= 60 ? AMBER : m <= 120 ? '#eab308' : GREEN);

  return (
    <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
      <div style={{ padding: '14px 16px', borderBottom: HAIR, display: 'flex', gap: '10px', alignItems: 'center' }}>
        <div style={{ ...h, marginBottom: 0, flex: 1 }}>Hours of service</div>
        <span style={{ fontSize: '11px', color: MUTED }}>{c.driving || 0} driving of {c.total || 0}</span>
      </div>
      <div style={{ display: 'flex', gap: '1px', background: 'rgba(255,255,255,0.06)' }}>
        {[
          ['Out of hours', c.outOfHours || 0, RED],
          ['Under 1h', c.under1h || 0, AMBER],
          ['1–2h left', c.under2h || 0, '#eab308'],
        ].map(([label, n, tone]) => (
          <div key={label} style={{ flex: 1, background: PANEL, padding: '11px 14px' }}>
            <div style={{ fontSize: '21px', fontWeight: 800, color: n ? tone : MUTED }}>{n}</div>
            <div style={{ ...lbl, marginTop: '1px' }}>{label}</div>
          </div>
        ))}
      </div>
      <div style={{ maxHeight: '250px', overflowY: 'auto' }}>
        {soon.length === 0 && (
          <div style={{ padding: '18px 16px', fontSize: '12px', color: MUTED, textAlign: 'center' }}>
            No drivers currently driving.
          </div>
        )}
        {soon.map((r, i) => (
          <div key={r.driverId || i} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '9px 16px', borderTop: HAIR }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: '13px', fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {r.driverName || '—'}
              </div>
              <div style={{ fontSize: '11px', color: MUTED }}>{r.vehicle ? `Truck ${r.vehicle}` : r.status || ''}</div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '14px', fontWeight: 800, color: toneFor(r.worstRemainingMin) }}>
                {hhmm(r.worstRemainingMin)}
              </div>
              <div style={{ ...lbl }}>left</div>
            </div>
            {r.timeUntilBreakMin != null && (
              <div style={{ textAlign: 'right', minWidth: '54px' }}>
                <div style={{ fontSize: '13px', fontWeight: 700, color: r.timeUntilBreakMin <= 30 ? AMBER : MUTED }}>
                  {hhmm(r.timeUntilBreakMin)}
                </div>
                <div style={lbl}>to break</div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------- where the fleet is ----------
function FleetState({ live }) {
  if (!live) return <div style={card}><div style={h}>Fleet</div><div style={{ fontSize: '12px', color: MUTED }}>Loading…</div></div>;
  const c = live.counts || {};
  const bars = [
    ['Moving', c.moving, GREEN],
    ['Idling', c.idling, AMBER],
    ['At terminal', c.atTerminal, MUTED],
    ['At customer', c.atCustomer, BLUE],
    ['At shop', c.atShop, '#a78bfa'],
    ['Parked', c.parked, MUTED],
  ];
  const max = Math.max(1, ...bars.map((b) => b[1] || 0));
  return (
    <div style={card}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', marginBottom: '12px' }}>
        <div style={{ ...h, marginBottom: 0, flex: 1 }}>Where the fleet is</div>
        <span style={{ fontSize: '11px', color: MUTED }}>{c.inService} in service of {c.total}</span>
      </div>
      {bars.map(([label, n, tone]) => (
        <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '7px' }}>
          <div style={{ width: '82px', fontSize: '11px', color: MUTED }}>{label}</div>
          <div style={{ flex: 1, height: '7px', background: 'rgba(255,255,255,0.06)', borderRadius: '4px', overflow: 'hidden' }}>
            <div style={{ width: `${((n || 0) / max) * 100}%`, height: '100%', background: tone, borderRadius: '4px' }} />
          </div>
          <div style={{ width: '28px', textAlign: 'right', fontSize: '13px', fontWeight: 700 }}>{n || 0}</div>
        </div>
      ))}
      {c.dormant > 0 && (
        <div style={{ marginTop: '11px', paddingTop: '10px', borderTop: HAIR, fontSize: '11px', color: MUTED, lineHeight: 1.5 }}>
          <b style={{ color: TXT }}>{c.dormant} dormant units</b> — no report in over 14 days.
          Excluded from alerts; worth checking whether they&apos;re still on subscription.
        </div>
      )}
    </div>
  );
}

// ---------- idling, because it's money ----------
function Idling({ live }) {
  const rows = (live && live.idlingNow) || [];
  if (!rows.length) return null;
  return (
    <div style={card}>
      <div style={{ ...h }}>Idling now · {rows.length}</div>
      <div style={{ fontSize: '11px', color: MUTED, marginBottom: '9px', lineHeight: 1.5 }}>
        Engine running while stationary. On reefer equipment this is fuel and hours burning with the truck still.
      </div>
      <div style={{ maxHeight: '150px', overflowY: 'auto' }}>
        {rows.slice(0, 12).map((r, i) => (
          <div key={i} style={{ display: 'flex', gap: '9px', padding: '5px 0', borderTop: i ? HAIR : 'none', fontSize: '12px' }}>
            <span style={{ fontWeight: 700, width: '52px' }}>{r.name}</span>
            <span style={{ color: MUTED, flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.place}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------- on the road ----------
function Moving({ live }) {
  const rows = ((live && live.rows) || []).filter((r) => r.state === 'moving')
    .sort((a, b) => (b.mph || 0) - (a.mph || 0));
  return (
    <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
      <div style={{ padding: '14px 16px', borderBottom: HAIR, display: 'flex', alignItems: 'center' }}>
        <div style={{ ...h, marginBottom: 0, flex: 1 }}>On the road</div>
        <span style={{ fontSize: '11px', color: MUTED }}>{rows.length}</span>
      </div>
      <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
        {rows.length === 0 && <div style={{ padding: '18px', fontSize: '12px', color: MUTED, textAlign: 'center' }}>Nothing moving.</div>}
        {rows.map((r, i) => (
          <div key={r.id || i} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '9px 16px', borderTop: i ? HAIR : 'none' }}>
            <span style={{ fontSize: '13px', fontWeight: 700, width: '54px' }}>{r.name}</span>
            <span style={{ fontSize: '12px', color: MUTED, flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {r.place}
            </span>
            <span style={{ fontSize: '13px', fontWeight: 800, color: (r.mph || 0) > 75 ? AMBER : GREEN }}>{r.mph}</span>
            <span style={{ ...lbl }}>mph</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------- the page ----------
export default function FleetOverview({ company }) {
  const [live, setLive] = useState(null);
  const [hos, setHos] = useState(null);
  const [attn, setAttn] = useState(null);
  const [errs, setErrs] = useState({});
  const [at, setAt] = useState(null);

  const refresh = useCallback(async () => {
    // Each panel loads independently — one failing endpoint must not blank the
    // whole dashboard, which is the usual way these screens become useless.
    const next = {};
    await Promise.all([
      loadLiveFleet().then(setLive).catch((e) => { next.live = e.message; }),
      loadHos().then(setHos).catch((e) => { next.hos = e.message; }),
      loadAttention().then(setAttn).catch((e) => { next.attn = e.message; }),
    ]);
    setErrs(next);
    setAt(new Date());
  }, []);

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 60000);
    return () => clearInterval(t);
  }, [refresh]);

  const c = (live && live.counts) || {};
  const hc = (hos && hos.counts) || {};
  const sum = (attn && attn.summary) || {};

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
      {/* headline numbers */}
      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
        <Metric label="Needs attention" value={(sum.critical || 0) + (sum.warning || 0)}
          tone={sum.critical ? RED : sum.warning ? AMBER : GREEN}
          sub={sum.critical ? `${sum.critical} critical` : 'all clear'} wide />
        <Metric label="Moving" value={c.moving ?? '—'} tone={GREEN} />
        <Metric label="Idling" value={c.idling ?? '—'} tone={c.idling ? AMBER : TXT} />
        <Metric label="Driving" value={hc.driving ?? '—'} sub={`${hc.total || 0} drivers`} />
        <Metric label="Under 1h left" value={hc.under1h ?? '—'} tone={hc.under1h ? AMBER : TXT} sub="hours of service" />
        <Metric label="At terminal" value={c.atTerminal ?? '—'} sub="available capacity" />
      </div>

      <Attention data={attn} err={errs.attn} />

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '14px' }}>
        <Hours hos={hos} err={errs.hos} />
        <FleetState live={live} />
        <Moving live={live} />
        <Idling live={live} />
      </div>

      <div style={{ textAlign: 'center', fontSize: '11px', color: MUTED, paddingTop: '2px' }}>
        {company ? `${company} · ` : ''}
        {at ? `updated ${at.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', second: '2-digit' })}` : 'loading…'}
        {' · refreshes every minute'}
      </div>
    </div>
  );
}
